/** @jsx jsx */
import { Button, Grid, useClipboard } from "@cd2/components"
import { useFileboy } from "@cd2/fileboy-react"
import { H4, P } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import { useState } from "react"
import { icons } from "../constants/icons"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { IRecord } from "../types/schema"
import { FloatingBox } from "./Misc/FloatingBox"

type OppRecord = IRecord<"quotes" | "orders", "projectRegistrationNumber"> & {
  opportunityCertificateFile?: IRecord<"files", "fileboyId" | "fileName">
}
export function OpportunityCertificatePreview({
  recordId,
  resource,
  className,
}: {
  recordId?: string
  resource: "quotes" | "orders"
  className?: string
}) {
  const fileboy = useFileboy()
  const skeem = window.skeem
  const [record, setRecord] = useState<OppRecord | null>(null)
  const clipboard = useClipboard()
  const alerts = useSiteAlerts()
  async function loadRecord() {
    if (!recordId) {
      setRecord(null)
      return
    }
    const record = await skeem.fetch2<OppRecord>({
      first: {
        name: resource,
        matches: { id: recordId },
        attributes: [
          "projectRegistrationNumber",
          { name: "opportunityCertificateFile", attributes: ["fileboyId", "fileName"] },
        ],
      },
    })
    if (record?.projectRegistrationNumber) {
      setRecord(record ?? null)
    } else {
      setRecord(null)
    }
  }

  useAsyncEffect(loadRecord, [recordId, resource])

  if (!record) {
    return null
  }

  return (
    <FloatingBox
      header={<H4>Opportunity Certificate</H4>}
      css={css`
        max-width: 500px;
        margin-left: auto;
        width: 100%;
      `}
      className={className}
      footer={
        record.opportunityCertificateFile?.fileboyId ? (
          <Grid gap={4} justifyItems="start">
            <div>
              <a
                href={fileboy.buildFileUrl(record.opportunityCertificateFile?.fileboyId, {
                  download: true,
                  filename: "Opportunity Certificate",
                })}
              >
                <Button leftIcon={icons.download}>Download opportunity certificate</Button>
              </a>
            </div>
          </Grid>
        ) : undefined
      }
    >
      <P weight={600}>Project Registration Num:</P>
      <Button
        leftIcon={icons.copy}
        onClick={() => {
          try {
            clipboard(record.projectRegistrationNumber)
            alerts.genericSuccess(`Copied ${record.projectRegistrationNumber} to clipboard`)
          } catch (e) {
            console.error(e)
          }
        }}
        buttonStyle="text"
      >
        {record.projectRegistrationNumber}
      </Button>
    </FloatingBox>
  )
}
